import { HttpErrorResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { patchState, signalStore, withState } from "@ngrx/signals";
import { Observable, finalize, shareReplay, tap } from "rxjs";
import { FileImageServiceResponse } from "../models/images/file-image-storage-response.interface";
import { FileImageService } from "../services/file-image.service";

export type FileImageBucketState = {
  usageByOst: Record<string, FileImageServiceResponse>;
  loadingByOst: Record<string, boolean>;
  errorStatusByOst: Record<string, number>;
  fetchedAtByOst: Record<string, number>;
};

const initialState: FileImageBucketState = {
  usageByOst: {},
  loadingByOst: {},
  errorStatusByOst: {},
  fetchedAtByOst: {},
};

@Injectable({ providedIn: "root" })
export class FileImageBucketStore extends signalStore({ protectedState: false }, withState<FileImageBucketState>(initialState)) {
  private readonly inFlightRequests = new Map<string, Observable<FileImageServiceResponse>>();

  constructor(private readonly fileImageService: FileImageService) {
    super();
  }

  getBucketUsageForOst(ostNum: string | number): FileImageServiceResponse | undefined {
    return this.usageByOst()[String(ostNum)];
  }

  isLoadingForOst(ostNum: string | number): boolean {
    return this.loadingByOst()[String(ostNum)] ?? false;
  }

  hasErrorForOst(ostNum: string | number): boolean {
    return this.errorStatusByOst()[String(ostNum)] !== undefined;
  }

  getErrorStatusForOst(ostNum: string | number): number | undefined {
    return this.errorStatusByOst()[String(ostNum)];
  }

  getFetchedAtForOst(ostNum: string | number): number | undefined {
    return this.fetchedAtByOst()[String(ostNum)];
  }

  getBucketUsageByOstNum(ostNum: string | number): Observable<FileImageServiceResponse> {
    const key = String(ostNum);
    const inFlightRequest = this.inFlightRequests.get(key);
    if (inFlightRequest) return inFlightRequest;

    const errorStatusByOst = { ...this.errorStatusByOst() };
    delete errorStatusByOst[key];
    patchState(this, {
      loadingByOst: { ...this.loadingByOst(), [key]: true },
      errorStatusByOst,
    });

    let request$: Observable<FileImageServiceResponse>;
    request$ = this.fileImageService.getInfoBucketByOstNum(ostNum).pipe(
      tap({
        next: (usage: FileImageServiceResponse) => {
          const currentErrorStatusByOst = { ...this.errorStatusByOst() };
          delete currentErrorStatusByOst[key];
          patchState(this, {
            usageByOst: { ...this.usageByOst(), [key]: usage },
            errorStatusByOst: currentErrorStatusByOst,
            fetchedAtByOst: { ...this.fetchedAtByOst(), [key]: Date.now() },
          });
        },
        error: (error: unknown) => {
          const httpStatusCode = error instanceof HttpErrorResponse ? error.status : 0;
          patchState(this, {
            errorStatusByOst: { ...this.errorStatusByOst(), [key]: httpStatusCode },
          });
        },
      }),
      finalize(() => {
        patchState(this, {
          loadingByOst: { ...this.loadingByOst(), [key]: false },
        });
        if (this.inFlightRequests.get(key) === request$) {
          this.inFlightRequests.delete(key);
        }
      }),
      shareReplay({ bufferSize: 1, refCount: false })
    );

    this.inFlightRequests.set(key, request$);
    return request$;
  }
}
